// frontend/src/components/ProblemList.tsx
import React from 'react';
import { Problem } from '../services/api';
import { ProblemCard } from './ProblemCard';
import { LoadingSpinner } from './LoadingSpinner';
import { ErrorMessage } from './ErrorMessage';
import { FloatingChatButton } from './FloatingChatButton';
import { ActiveProblemProvider } from '../contexts/ActiveProblemContext';

interface ProblemListProps {
  problems: Problem[];
  loading: boolean;
  error: string | null;
  startIndex?: number;
  onRetry?: () => void;
}

export const ProblemList: React.FC<ProblemListProps> = ({
  problems,
  loading,
  error,
  startIndex = 0,
  onRetry
}) => {
  if (loading) {
    return <LoadingSpinner size="large" message="Loading problems..." className="py-20" />;
  }

  if (error) {
    return <ErrorMessage message={error} onRetry={onRetry} />;
  }

  if (problems.length === 0) {
    return (
      <div className="text-center py-20">
        <div className="mb-4">
          <svg className="mx-auto h-16 w-16 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        </div>
        <h3 className="text-xl font-semibold text-gray-600 mb-4">No problems found</h3>
        <p className="text-gray-500 mb-6">Try adjusting your search criteria or generate new problems.</p>
        {onRetry && (
          <button onClick={onRetry} className="btn">
            Refresh Problems
          </button>
        )}
      </div>
    );
  }

  return (
    <ActiveProblemProvider>
      <div className="space-y-6">
        {problems.map((problem, index) => (
          <ProblemCard
            key={problem.id}
            problem={problem}
            index={startIndex + index}
            showTooltips={index === 0} // Only show first-time tooltips on the first card
          />
        ))}

        {/* Floating AI Chat Button - appears when user interacts with a problem */}
        <FloatingChatButton />
      </div>
    </ActiveProblemProvider>
  );
};
